import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { OperationalKpisDashboardDto } from 'api/dashboard/dtos/OperationalKpisDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import styles from '../kpiCards/KpiCards.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';

type Props = {
	data?: OperationalKpisDashboardDto | null;
	isLoading?: boolean;
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const RESERVATIONS_FILTERS_CACHE_KEY = 'RESERVATIONSFILTER';
const REQUESTS_FILTERS_CACHE_KEY = 'REQUESTSFILTER';

const OperationalKpis = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<OperationalKpisDashboardDto | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);

	const isLegacyMode = props.data !== undefined;

	const navigateToReservations = (
		tab: 'reservations' | 'requests' | 'calendars',
		filtersOverride?: Record<string, any>,
	) => {
		try {
			if (filtersOverride) {
				const cacheKey = tab === 'requests' ? REQUESTS_FILTERS_CACHE_KEY : RESERVATIONS_FILTERS_CACHE_KEY;
				localStorage.setItem(cacheKey, JSON.stringify(filtersOverride));
			}
		} catch { /* silent */ }
		const query = tab !== 'calendars' ? `?tab=${tab}` : '';
		window.open(`/reservations${query}`, '_blank');
	};

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: {};
				const result = await DashboardService.getOperationalKpis(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const d = data ?? {
		availableForReservation: 0,
		withoutReservationNow: 0,
		availabilityRate: 0,
		pendingRequests: 0,
		reservableCount: 0,
		immobilizedCount: 0,
		inMaintenanceCount: 0,
		reservedCount: 0,
	};

	if (isLoading) {
		return (
			<Box
				className={tableStyles.container}
				title={t('dashboard.operational_kpis') as string}
				subTitleTooltip={t('dashboard.operational_kpis_tooltip' as any) as string}
			>
				<div className={styles.centerAlign}><LoadingSpinner /></div>
			</Box>
		);
	}

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.operational_kpis') as string}
			subTitleTooltip={t('dashboard.operational_kpis_tooltip' as any) as string}
		>
			<div className={styles.kpiRow}>
				<div className={`${styles.kpiItem} ${styles.kpiItemClickable}`} onClick={() => navigateToReservations('calendars')}>
					<div className={styles.kpiInline}>
						<div className={styles.kpiLabel}>{t('dashboard.op_kpi_available_assets' as any)}</div>
						<div className={`${styles.kpiValue} ${styles.teal}`}>{d.withoutReservationNow}</div>
					</div>
				</div>
				<div className={`${styles.kpiItem} ${styles.kpiItemClickable} ${styles.kpiStack}`} onClick={() => navigateToReservations('reservations')}>
					<div className={styles.kpiInline}>
						<div className={styles.kpiLabel}>{t('dashboard.op_kpi_unavailable_assets' as any)}</div>
						<div className={`${styles.kpiValue} ${styles.red}`}>{d.immobilizedCount + d.inMaintenanceCount + d.reservedCount}</div>
					</div>
					<div className={styles.kpiBreakdown}>
						<span
							className={`${styles.kpiBreakdownValue} ${styles.orange}`}
							data-tip={t('dashboard.op_kpi_immobilized' as any) as string}
							data-for="op-kpi-immobilized-tooltip"
						>
							{d.immobilizedCount}
						</span>
						<span
							className={`${styles.kpiBreakdownValue} ${styles.amber}`}
							data-tip={t('dashboard.op_kpi_in_maintenance' as any) as string}
							data-for="op-kpi-in-maintenance-tooltip"
						>
							{d.inMaintenanceCount}
						</span>
						<span
							className={`${styles.kpiBreakdownValue} ${styles.green}`}
							data-tip={t('dashboard.op_kpi_reserved_now' as any) as string}
							data-for="op-kpi-reserved-now-tooltip"
						>
							{d.reservedCount}
						</span>
						<ReactTooltip id="op-kpi-immobilized-tooltip" effect="solid" />
						<ReactTooltip id="op-kpi-in-maintenance-tooltip" effect="solid" />
						<ReactTooltip id="op-kpi-reserved-now-tooltip" effect="solid" />
					</div>
				</div>
				<div className={`${styles.kpiItem} ${styles.kpiItemClickable}`} onClick={() => navigateToReservations('calendars')}>
					<div className={styles.kpiInline}>
						<div className={styles.kpiLabel}>{t('dashboard.op_kpi_availability_rate' as any)}</div>
						<div className={`${styles.kpiValue} ${styles.blue}`}>{Number(d.availabilityRate).toFixed(0)}%</div>
					</div>
				</div>
				<div className={`${styles.kpiItem} ${styles.kpiItemClickable}`} onClick={() => navigateToReservations('requests', { status: 'PENDING_APPROVAL' })}>
					<div className={styles.kpiInline}>
						<div className={styles.kpiLabel}>{t('dashboard.op_kpi_pending_requests' as any)}</div>
						<div className={`${styles.kpiValue} ${styles.amber}`}>{d.pendingRequests}</div>
					</div>
				</div>
			</div>
		</Box>
	);
};

export default OperationalKpis;
